import { useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { CollectionPage, FilterInput } from "@/components/layout/CollectionPage";
import { DocumentList } from "@/components/dashboard/DocumentList";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FilePlus2, X } from "lucide-react";
import { useTags } from "@/hooks/use-tags";
import {
  useDocuments,
  useDocumentTypes,
  useFolders,
  type DocumentFilters,
  type FolderSelection,
} from "@/hooks/use-library";

type SortKey = "recent" | "title" | "longest";

const SORT_LABELS: Record<SortKey, string> = {
  recent: "Recently edited",
  title: "Title A–Z",
  longest: "Longest first",
};

/** Everything the view needs, read from the URL so it survives a reload and a share. */
interface LibraryQuery extends DocumentFilters {
  folder: FolderSelection;
  sort: SortKey;
}

function parseQuery(search: string): LibraryQuery {
  const params = new URLSearchParams(search);

  const folderParam = params.get("folder");
  let folder: FolderSelection = "all";
  if (folderParam === "unfiled") folder = "unfiled";
  else if (folderParam) {
    const id = parseInt(folderParam, 10);
    if (!Number.isNaN(id)) folder = id;
  }

  const tagParam = params.get("tag");
  const tagId = tagParam ? parseInt(tagParam, 10) : NaN;

  const sort = params.get("sort");

  return {
    folder,
    docType: params.get("type"),
    tagId: Number.isNaN(tagId) ? null : tagId,
    archived: params.get("archived") === "true",
    sort: sort === "title" || sort === "longest" ? sort : "recent",
  };
}

function toSearch(query: LibraryQuery): string {
  const params = new URLSearchParams();
  if (query.folder === "unfiled") params.set("folder", "unfiled");
  else if (typeof query.folder === "number") params.set("folder", String(query.folder));
  if (query.docType) params.set("type", query.docType);
  if (query.tagId) params.set("tag", String(query.tagId));
  if (query.archived) params.set("archived", "true");
  if (query.sort !== "recent") params.set("sort", query.sort);
  const qs = params.toString();
  return qs ? `/library?${qs}` : "/library";
}

/**
 * The library owns every way of narrowing the document list — folder, type,
 * tag, archive and sort — so the dashboard does not have to.
 *
 * Filters live in the URL rather than component state: the sidebar links
 * straight into a folder here, and the state a writer set up should still be
 * there after a reload.
 */
export default function LibraryPage() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const query = useMemo(() => parseQuery(search), [search]);

  const { data: documents = [], isLoading } = useDocuments({
    folder: query.folder,
    docType: query.docType,
    tagId: query.tagId,
    archived: query.archived,
  });
  const { data: folders = [] } = useFolders();
  const { data: types = [] } = useDocumentTypes();
  const { tags } = useTags();

  const update = (patch: Partial<LibraryQuery>) =>
    navigate(toSearch({ ...query, ...patch }));

  // Narrows what is already loaded. The server filters are in the URL because
  // they are a view worth sharing; this one is throwaway typing.
  const [filterText, setFilterText] = useState("");

  const sorted = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    const rows = needle
      ? documents.filter((document) =>
          (document.title || "Untitled").toLowerCase().includes(needle),
        )
      : [...documents];
    if (query.sort === "title") {
      return rows.sort((a, b) =>
        (a.title || "Untitled").localeCompare(b.title || "Untitled"),
      );
    }
    if (query.sort === "longest") {
      return rows.sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
    }
    return rows.sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  }, [documents, query.sort, filterText]);

  const activeFolder =
    typeof query.folder === "number"
      ? folders.find((folder) => folder.id === query.folder)
      : undefined;

  const scopeLabel =
    query.folder === "unfiled"
      ? "No folder"
      : activeFolder
        ? activeFolder.name
        : query.archived
          ? "Archived"
          : "All documents";

  const hasFilters =
    query.folder !== "all" || !!query.docType || !!query.tagId || query.archived;

  return (
    <CollectionPage
      title={scopeLabel}
      count={sorted.length}
      noun="document"
      isLoading={isLoading}
      action={
        <Link href="/documents/create">
          <Button>
            <FilePlus2 className="mr-2 h-4 w-4" />
            New document
          </Button>
        </Link>
      }
      filters={
        <>
          <FilterInput
            value={filterText}
            onChange={setFilterText}
            placeholder="Filter documents…"
          />

          <Select
            value={query.docType || "all"}
            onValueChange={(value) => update({ docType: value === "all" ? null : value })}
          >
            <SelectTrigger className="h-9 w-auto min-w-[8.5rem] gap-2">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any type</SelectItem>
              {types.map((type) => (
                <SelectItem key={type.id} value={type.name}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={query.tagId ? String(query.tagId) : "all"}
            onValueChange={(value) =>
              update({ tagId: value === "all" ? null : parseInt(value, 10) })
            }
          >
            <SelectTrigger className="h-9 w-auto min-w-[8.5rem] gap-2">
              <SelectValue placeholder="Any tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any tag</SelectItem>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={String(tag.id)}>
                  #{tag.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={query.sort}
            onValueChange={(value) => update({ sort: value as SortKey })}
          >
            <SelectTrigger className="h-9 w-auto min-w-[9.5rem] gap-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {SORT_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={query.archived ? "secondary" : "ghost"}
            size="sm"
            className="h-9"
            onClick={() => update({ archived: !query.archived })}
            aria-pressed={query.archived}
          >
            Archived
          </Button>

          {(hasFilters || filterText) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-muted-foreground"
              onClick={() => {
                setFilterText("");
                navigate("/library");
              }}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}

          {/* Tags are a filter, so managing them belongs beside the filter
              rather than in primary navigation. */}
          <Link href="/tags">
            <span className="ml-auto cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Manage tags →
            </span>
          </Link>
        </>
      }
    >
      <DocumentList
        documents={sorted}
        folders={folders}
        archived={query.archived}
        // Redundant when the list is already scoped to one folder.
        showFolder={query.folder === "all"}
        isLoading={isLoading}
        emptyTitle={
          query.archived
            ? "Nothing archived."
            : hasFilters || filterText
              ? "Nothing matches these filters."
              : "No documents yet."
        }
        emptyHint={
          (hasFilters || filterText) && !query.archived
            ? "Try clearing a filter."
            : undefined
        }
        showCreateOnEmpty={!hasFilters && !filterText}
      />
    </CollectionPage>
  );
}
